import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { onboardingAPI, teamsV2API, usersAPI, parrainageAPI } from '@/lib/api';
import { OverrideChainSimulator } from '@/components/admin/OverrideChainSimulator';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    UserPlus, Mail, Phone, GraduationCap, Loader2, CheckCircle2, XCircle,
    Clock, Calendar, Award, Filter, RotateCcw, UserX, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const BUCKETS = [
    { key: 'inscrit', label: 'Nouveaux inscrits', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
    { key: 'en_formation', label: 'En formation', color: 'bg-amber-100 text-amber-700', icon: GraduationCap },
    { key: 'formation_terminee', label: 'Formation terminée / Entretien', color: 'bg-purple-100 text-purple-700', icon: Clock },
    { key: 'valide', label: 'Validés', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    { key: 'refuse', label: 'Refusés', color: 'bg-rose-100 text-rose-700', icon: XCircle },
    { key: 'inactif', label: 'Inactifs', color: 'bg-slate-200 text-slate-600', icon: UserX },
];

const initials = (n) => (n || '').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

export default function OnboardingAdmin() {
    const { user, isAdmin, isVP } = useAuth();
    const canManage = isAdmin();
    const canValidate = isAdmin() || isVP();
    const isVpOnly = isVP() && !isAdmin();
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeBucket, setActiveBucket] = useState('formation_terminee');
    // 🎯 Focus depuis Uatu / deep-links : /onboarding?focus=<user_id>&status=<bucket>&filter=<responsable>
    const [searchParams] = useSearchParams();
    const focusId = searchParams.get('focus');
    const initialBucket = searchParams.get('status');
    const initialResponsable = searchParams.get('filter');
    const focusCardRef = useRef(null);
    const [highlightedId, setHighlightedId] = useState(null);

    // Applique le bucket initial depuis l'URL (une seule fois au mount)
    useEffect(() => {
        if (initialBucket && BUCKETS.some(b => b.key === initialBucket) && initialBucket !== activeBucket) {
            setActiveBucket(initialBucket);
        }
    }, [initialBucket]); // eslint-disable-line react-hooks/exhaustive-deps

    // Filtres
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [minProgress, setMinProgress] = useState('');
    const [minQuiz, setMinQuiz] = useState('');
    const [responsable, setResponsable] = useState(initialResponsable && initialResponsable !== 'all' ? initialResponsable : 'all');

    // Données pour la validation
    const [teams, setTeams] = useState([]);
    const [vps, setVps] = useState([]);

    // Dialogs
    const [validateTarget, setValidateTarget] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [assignTarget, setAssignTarget] = useState(null);
    const [valTeam, setValTeam] = useState('');
    const [valVp, setValVp] = useState('');
    const [valPwMode, setValPwMode] = useState('keep');
    // Sélection du rôle final : par défaut apporteur.
    const [valRole, setValRole] = useState('apporteur');
    // Champs conditionnels quand role = parrain
    const [valParrainLevel, setValParrainLevel] = useState('1');
    const [valParrainBranch, setValParrainBranch] = useState('');
    const [valParrainParentId, setValParrainParentId] = useState('');
    const [valParrainEpOverride, setValParrainEpOverride] = useState('');
    const [valParrainChOverride, setValParrainChOverride] = useState('');
    const [availableParrains, setAvailableParrains] = useState([]);
    const [rejectReason, setRejectReason] = useState('');
    const [assignTeam, setAssignTeam] = useState('');
    const [assignVp, setAssignVp] = useState('');
    const [acting, setActing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (minProgress) params.min_progress = Number(minProgress);
            if (minQuiz) params.min_quiz = Number(minQuiz);
            if (responsable && responsable !== 'all') params.responsable = responsable;
            const res = await onboardingAPI.listCandidates(params);
            setCandidates(res.data.candidates || []);
        } catch (e) {
            toast.error('Erreur de chargement des candidats');
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, minProgress, minQuiz, responsable]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        teamsV2API.getAll().then((r) => {
            const t = Array.isArray(r.data) ? r.data : (r.data.teams || []);
            setTeams(t);
        }).catch(() => {});
        usersAPI.getVPs().then((r) => {
            setVps(Array.isArray(r.data) ? r.data : (r.data.vps || r.data.users || []));
        }).catch(() => {});
    }, []);

    const counts = useMemo(() => {
        const c = {};
        BUCKETS.forEach((b) => { c[b.key] = 0; });
        candidates.forEach((cand) => { c[cand.effective_status] = (c[cand.effective_status] || 0) + 1; });
        return c;
    }, [candidates]);

    const visible = useMemo(
        () => candidates.filter((c) => c.effective_status === activeBucket),
        [candidates, activeBucket]
    );

    // 🎯 Auto-focus depuis Uatu : sélectionne le bucket du user ciblé + surligne + scroll
    useEffect(() => {
        if (!focusId || candidates.length === 0) return;
        const target = candidates.find((c) => c.id === focusId);
        if (!target) {
            toast.warning("Cet apprenant n'est pas dans la liste actuelle.");
            return;
        }
        if (target.effective_status && target.effective_status !== activeBucket) {
            setActiveBucket(target.effective_status);
        }
        setHighlightedId(focusId);
        setTimeout(() => {
            focusCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
        const t = setTimeout(() => setHighlightedId(null), 4500);
        return () => clearTimeout(t);
    }, [focusId, candidates]);

    const resetFilters = () => {
        setDateFrom(''); setDateTo(''); setMinProgress(''); setMinQuiz(''); setResponsable('all');
    };

    const openValidate = (cand) => {
        setValidateTarget(cand);
        setValTeam('');
        setValVp(cand.vp_id || (isVpOnly ? (user?.id || '') : ''));
        setValPwMode('keep');
        setValRole('apporteur');
        setValParrainLevel('1');
        setValParrainBranch('');
        setValParrainParentId('');
        setValParrainEpOverride('');
        setValParrainChOverride('');
    };

    // Charge les parrains disponibles du VP sélectionné (pour choisir un parent en N2/N3)
    useEffect(() => {
        if (valRole !== 'parrain' || !valVp) { setAvailableParrains([]); return; }
        parrainageAPI.listParrains(valVp)
            .then((r) => setAvailableParrains(Array.isArray(r.data) ? r.data : (r.data?.parrains || [])))
            .catch(() => setAvailableParrains([]));
    }, [valRole, valVp]);

    const submitValidate = async () => {
        // Validations côté client pour un retour immédiat
        if (valRole === 'parrain') {
            if (!valVp) { toast.error('Sélectionnez un VP pour le rattachement Parrain'); return; }
            const lvl = parseInt(valParrainLevel, 10) || 1;
            if (lvl === 1 && !valParrainBranch.trim()) { toast.error('Renseignez un nom de branche pour un Parrain Niveau 1'); return; }
            if (lvl > 1 && !valParrainParentId) { toast.error('Sélectionnez un parrain de rattachement'); return; }
        }
        setActing(true);
        try {
            const payload = {
                team_id: valTeam || null,
                vp_id: valVp || null,
                password_mode: valPwMode,
                role: valRole,
            };
            if (valRole === 'parrain') {
                const lvl = parseInt(valParrainLevel, 10) || 1;
                payload.parrain_level = lvl;
                payload.parrain_branch = lvl === 1 ? valParrainBranch.trim() : null;
                payload.parrain_parent_id = lvl > 1 ? valParrainParentId : null;
                payload.commission_percent_ep_override = valParrainEpOverride === '' ? null : parseFloat(valParrainEpOverride);
                payload.commission_percent_ch_override = valParrainChOverride === '' ? null : parseFloat(valParrainChOverride);
            }
            await onboardingAPI.validate(validateTarget.id, payload);
            const roleLabel = valRole === 'parrain' ? 'Parrain' : (valRole === 'vp' ? 'VP' : 'Apporteur Actif');
            toast.success(`${validateTarget.first_name} validé → ${roleLabel}`);
            setValidateTarget(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setActing(false);
        }
    };

    const submitReject = async () => {
        setActing(true);
        try {
            await onboardingAPI.reject(rejectTarget.id, { reason: rejectReason });
            toast.success(`${rejectTarget.first_name} refusé`);
            setRejectTarget(null); setRejectReason('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors du refus');
        } finally {
            setActing(false);
        }
    };

    const openAssign = (cand) => {
        setAssignTarget(cand);
        setAssignTeam(''); setAssignVp(cand.vp_id || '');
    };

    const submitAssign = async () => {
        if (!assignVp) { toast.error('Sélectionnez un VP responsable'); return; }
        setActing(true);
        try {
            await onboardingAPI.assign(assignTarget.id, { team_id: assignTeam || null, vp_id: assignVp });
            toast.success(`${assignTarget.first_name} affecté à l'équipe`);
            setAssignTarget(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur lors de l\'affectation');
        } finally {
            setActing(false);
        }
    };

    if (!isAdmin() && !isVP()) {
        return (
            <DashboardLayout>
                <div className="text-center py-20 text-slate-500" data-testid="onboarding-forbidden">
                    Accès réservé aux administrateurs.
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="onboarding-admin-page">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
                        <UserPlus className="w-7 h-7 text-purple-600" /> Onboarding
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {canManage
                            ? 'Gérez le parcours des candidats : formation, entretien, validation.'
                            : 'Suivez la progression des apprenants de votre équipe.'}
                    </p>
                </div>

                {/* Buckets */}
                <div className="flex flex-wrap gap-2" data-testid="onboarding-buckets">
                    {BUCKETS.map((b) => {
                        const Icon = b.icon;
                        const active = activeBucket === b.key;
                        return (
                            <button
                                key={b.key}
                                onClick={() => setActiveBucket(b.key)}
                                data-testid={`bucket-${b.key}`}
                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
                                    active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {b.label}
                                <span className={`ml-1 rounded-full px-2 py-0.5 text-xs ${active ? 'bg-white/20' : b.color}`}>{counts[b.key] || 0}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Filtres */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
                            <Filter className="w-4 h-4" /> Filtres
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Inscrit depuis</Label>
                                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="filter-date-from" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Inscrit jusqu'au</Label>
                                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="filter-date-to" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Progression min (%)</Label>
                                <Input type="number" min="0" max="100" value={minProgress} onChange={(e) => setMinProgress(e.target.value)} className="h-9" data-testid="filter-min-progress" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Score quiz min (%)</Label>
                                <Input type="number" min="0" max="100" value={minQuiz} onChange={(e) => setMinQuiz(e.target.value)} className="h-9" data-testid="filter-min-quiz" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Responsable (VP)</Label>
                                <Select value={responsable} onValueChange={setResponsable} disabled={!canManage}>
                                    <SelectTrigger className="h-9" data-testid="filter-responsable"><SelectValue placeholder="Tous" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        {vps.map((v) => (
                                            <SelectItem key={v.id} value={v.id}>{v.first_name} {v.last_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end">
                                <Button variant="outline" onClick={resetFilters} className="h-9 gap-1.5" data-testid="reset-filters-btn">
                                    <RotateCcw className="w-4 h-4" /> Réinitialiser
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Liste */}
                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
                ) : visible.length === 0 ? (
                    <div className="text-center py-16 text-slate-400" data-testid="onboarding-empty">Aucun candidat dans cette catégorie.</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="candidate-list">
                        {visible.map((c) => {
                            const isFocused = highlightedId === c.id;
                            return (
                            <Card
                                key={c.id}
                                ref={isFocused ? focusCardRef : null}
                                className={`hover:shadow-md transition-all ${isFocused ? 'ring-4 ring-violet-400 ring-offset-2 shadow-xl scale-[1.01] animate-pulse' : ''}`}
                                data-testid={`candidate-card-${c.id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        <Avatar className="h-11 w-11">
                                            <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold">
                                                {initials(`${c.first_name} ${c.last_name}`)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-slate-900 truncate">{c.first_name} {c.last_name}</h3>
                                                {c.responsable_name && <Badge variant="outline" className="text-xs">VP : {c.responsable_name}</Badge>}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                                                <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {c.email}</span>
                                                {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {c.phone}</span>}
                                            </div>
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                                                <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Inscrit le {fmtDate(c.created_at)}</span>
                                                <span className="inline-flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {c.progress_pct}% ({c.completed_modules}/{c.total_modules})</span>
                                                <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Quiz : {c.avg_quiz_score != null ? `${c.avg_quiz_score}%` : '—'}</span>
                                            </div>
                                            {/* Progress bar */}
                                            <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                                                <div className="h-full bg-purple-500" style={{ width: `${c.progress_pct || 0}%` }} />
                                            </div>

                                            {/* Actions */}
                                            {['valide'].includes(c.effective_status) ? (
                                                <p className="mt-3 text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Validé le {fmtDate(c.validated_at)} → Apporteur</p>
                                            ) : c.effective_status === 'refuse' ? (
                                                <p className="mt-3 text-xs text-rose-600 inline-flex items-center gap-1"><XCircle className="w-4 h-4" /> Refusé le {fmtDate(c.rejected_at)}</p>
                                            ) : canValidate ? (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {canManage && (
                                                        <Button size="sm" variant="outline" onClick={() => openAssign(c)} className="gap-1.5" data-testid={`assign-btn-${c.id}`}>
                                                            <Users className="w-4 h-4" /> {c.vp_id ? 'Réaffecter' : 'Assigner'}
                                                        </Button>
                                                    )}
                                                    <Button size="sm" onClick={() => openValidate(c)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid={`validate-btn-${c.id}`}>
                                                        <CheckCircle2 className="w-4 h-4" /> Valider
                                                    </Button>
                                                    {canManage && (
                                                        <Button size="sm" variant="outline" onClick={() => setRejectTarget(c)} className="text-rose-600 border-rose-200 hover:bg-rose-50 gap-1.5" data-testid={`reject-btn-${c.id}`}>
                                                            <XCircle className="w-4 h-4" /> Refuser
                                                        </Button>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="mt-3 text-xs text-slate-400 inline-flex items-center gap-1" data-testid={`vp-readonly-${c.id}`}>
                                                    <Clock className="w-4 h-4" /> En formation — suivi
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Dialog Validation */}
            <Dialog open={!!validateTarget} onOpenChange={(o) => !o && setValidateTarget(null)}>
                <DialogContent data-testid="validate-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Valider {validateTarget?.first_name} {validateTarget?.last_name}</DialogTitle>
                        <DialogDescription>
                            Choisissez le rôle attribué au candidat. L'accès CRM sera débloqué en conséquence.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Sélecteur de rôle : Admin voit Apporteur/Parrain/VP ; VP voit Apporteur/Parrain */}
                        <div className="space-y-1.5">
                            <Label>Rôle attribué</Label>
                            <Select value={valRole} onValueChange={setValRole}>
                                <SelectTrigger data-testid="validate-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="apporteur" data-testid="role-option-apporteur">Apporteur</SelectItem>
                                    <SelectItem value="parrain" data-testid="role-option-parrain">Parrain (Réseau Parrainage)</SelectItem>
                                    {canManage && (
                                        <SelectItem value="vp" data-testid="role-option-vp">VP</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                            {isVpOnly && (
                                <p className="text-[11px] text-slate-500">Un VP peut valider uniquement en Apporteur ou Parrain. Seul un Admin peut attribuer le rôle VP.</p>
                            )}
                        </div>

                        {/* Équipe : uniquement pour Apporteur (les parrains sont dans la hiérarchie parrainage, un nouveau VP n'a pas d'équipe à l'entrée) */}
                        {valRole === 'apporteur' && (
                            <div className="space-y-1.5">
                                <Label>Équipe</Label>
                                <Select value={valTeam} onValueChange={setValTeam}>
                                    <SelectTrigger data-testid="validate-team-select"><SelectValue placeholder="Sélectionner une équipe" /></SelectTrigger>
                                    <SelectContent>
                                        {teams.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Responsable (VP) : Apporteur + Parrain. VP → auto = self */}
                        {(valRole === 'apporteur' || valRole === 'parrain') && (
                            <div className="space-y-1.5">
                                <Label>{valRole === 'parrain' ? 'VP de rattachement (Réseau Parrainage)' : 'Responsable (VP)'}</Label>
                                <Select value={valVp} onValueChange={setValVp} disabled={isVpOnly}>
                                    <SelectTrigger data-testid="validate-vp-select"><SelectValue placeholder="Sélectionner un VP" /></SelectTrigger>
                                    <SelectContent>
                                        {vps.map((v) => (<SelectItem key={v.id} value={v.id}>{v.first_name} {v.last_name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                                {isVpOnly && (
                                    <p className="text-[11px] text-slate-400">Rattachement automatique à votre équipe.</p>
                                )}
                            </div>
                        )}

                        {/* Champs conditionnels Parrain */}
                        {valRole === 'parrain' && (
                            <div className="space-y-3 rounded-md border border-indigo-200 bg-indigo-50/50 p-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Niveau</Label>
                                        <Select value={valParrainLevel} onValueChange={(v) => { setValParrainLevel(v); setValParrainParentId(''); }}>
                                            <SelectTrigger data-testid="validate-parrain-level"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1">Niveau 1 (Direct)</SelectItem>
                                                <SelectItem value="2">Niveau 2</SelectItem>
                                                <SelectItem value="3">Niveau 3</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {valParrainLevel === '1' ? (
                                        <div className="space-y-1.5">
                                            <Label className="text-xs">Branche</Label>
                                            <Input
                                                value={valParrainBranch}
                                                onChange={(e) => setValParrainBranch(e.target.value)}
                                                placeholder="Ex : Nord, Sud, Ouest…"
                                                data-testid="validate-parrain-branch"
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5">
                                            <Label className="text-xs">Parrain de rattachement</Label>
                                            <Select value={valParrainParentId} onValueChange={setValParrainParentId}>
                                                <SelectTrigger data-testid="validate-parrain-parent"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                                                <SelectContent>
                                                    {availableParrains
                                                        .filter((p) => Number(p.parrain_level || 0) < Number(valParrainLevel))
                                                        .map((p) => (
                                                            <SelectItem key={p.id} value={p.id}>
                                                                N{p.parrain_level} · {p.first_name} {p.last_name}
                                                            </SelectItem>
                                                        ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                                {canManage ? (
                                    <>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1.5">
                                                <Label className="text-xs">Taux Courtage override (%)</Label>
                                                <Input
                                                    type="number" step="0.1" min="14" max="100"
                                                    disabled={valParrainLevel !== '1'}
                                                    value={valParrainEpOverride}
                                                    onChange={(e) => setValParrainEpOverride(e.target.value)}
                                                    placeholder={valParrainLevel === '1' ? '(vide = taux global · ≥ 14)' : 'N1 uniquement'}
                                                    data-testid="validate-parrain-ep-override"
                                                />
                                            </div>
                                            <div className="space-y-1.5">
                                                <Label className="text-xs">Taux Travaux d’optimisation override (%)</Label>
                                                <Input
                                                    type="number" step="0.1" min="8" max="100"
                                                    disabled={valParrainLevel !== '1'}
                                                    value={valParrainChOverride}
                                                    onChange={(e) => setValParrainChOverride(e.target.value)}
                                                    placeholder={valParrainLevel === '1' ? '(vide = taux global · ≥ 8)' : 'N1 uniquement'}
                                                    data-testid="validate-parrain-ch-override"
                                                />
                                            </div>
                                        </div>
                                        <p className="text-[11px] text-indigo-700">
                                            {valParrainLevel === '1'
                                                ? "Override N1 = budget TOTAL sur toute la branche. Doit être ≥ 14% Courtage et ≥ 8% Travaux d’optimisation."
                                                : "Un N2/N3 utilise toujours les taux globaux — pas de personnalisation possible."}
                                        </p>
                                        {valParrainLevel === '1' && (
                                            <OverrideChainSimulator
                                                epOverride={valParrainEpOverride}
                                                chOverride={valParrainChOverride}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <p className="text-[11px] text-slate-600 bg-indigo-50/60 border border-indigo-100 rounded p-2 leading-relaxed" data-testid="validate-parrain-override-vp-notice">
                                        Ce parrain sera validé aux <b>taux globaux du réseau</b>. Si vous souhaitez lui donner plus, demandez à un administrateur.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Mot de passe</Label>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="pwmode" checked={valPwMode === 'keep'} onChange={() => setValPwMode('keep')} data-testid="pw-keep" />
                                    Conserver le mot de passe actuel
                                </label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="pwmode" checked={valPwMode === 'reset'} onChange={() => setValPwMode('reset')} data-testid="pw-reset" />
                                    Forcer la définition d'un nouveau mot de passe (email envoyé)
                                </label>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setValidateTarget(null)}>Annuler</Button>
                        <Button onClick={submitValidate} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-validate-btn">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider le candidat'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Affectation (Admin) — rattacher un apprenant en formation à une équipe + VP */}
            <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
                <DialogContent data-testid="assign-dialog">
                    <DialogHeader>
                        <DialogTitle>Affecter {assignTarget?.first_name} {assignTarget?.last_name}</DialogTitle>
                        <DialogDescription>
                            Rattachez ce candidat à une équipe et un VP responsable. Le VP pourra suivre sa progression pendant la formation (le rôle reste « Apprenant »).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Équipe</Label>
                            <Select value={assignTeam} onValueChange={setAssignTeam}>
                                <SelectTrigger data-testid="assign-team-select"><SelectValue placeholder="Sélectionner une équipe" /></SelectTrigger>
                                <SelectContent>
                                    {teams.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Responsable (VP)</Label>
                            <Select value={assignVp} onValueChange={setAssignVp}>
                                <SelectTrigger data-testid="assign-vp-select"><SelectValue placeholder="Sélectionner un VP" /></SelectTrigger>
                                <SelectContent>
                                    {vps.map((v) => (<SelectItem key={v.id} value={v.id}>{v.first_name} {v.last_name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignTarget(null)}>Annuler</Button>
                        <Button onClick={submitAssign} disabled={acting} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-assign-btn">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Affecter'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Refus */}
            <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
                <DialogContent data-testid="reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Refuser {rejectTarget?.first_name} {rejectTarget?.last_name} ?</DialogTitle>
                        <DialogDescription>Le compte sera désactivé. Vous pouvez préciser un motif (optionnel).</DialogDescription>
                    </DialogHeader>
                    <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motif du refus (optionnel)" data-testid="reject-reason-input" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectTarget(null)}>Annuler</Button>
                        <Button onClick={submitReject} disabled={acting} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-btn">
                            {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer le refus'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
